import { find, findIndex } from 'lodash';

import { getFilterOptions, getGamesReports } from '../../../api';
import { onRequest } from '../..';
import { ActionsType, PropertyType, formatToISO } from '../../../common';
import { getTemplates } from '../../../api/endpoints/getTemplates';
import { createTemplate } from '../../../api/endpoints/addTemplate';
import { editTemplate } from '../../../api/endpoints/editTemplate';
import { getTemplateById } from '../../../api/endpoints/getTemplateById';
import { deleteTemplate } from '../../../api/endpoints/deleteTemplate';
import { getAverageRates } from '../../../api/endpoints/getAverageRates';

const reportsInit = async (payload: PropertyType) => {
	const { token, page } = payload;
	const reports = await getGamesReports({ token, page, query: '' });

	return { reports, currentPage: page, reportsLoading: false };
};

const partnersInit = (user: PropertyType) => {
	const { partners } = user;

	return { partners };
};

const ratesInit = async (payload: PropertyType) => {
	const averageRates = await getAverageRates(payload);

	return { averageRates };
};

const templatesInit = async (payload: PropertyType) => {
	const { token, userId } = payload;
	const templates = await getTemplates({ token, userId, report_name: 'games_report' });
	return { templates };
};

const templatesAdd = async (payload: PropertyType) => {
	const { token, userId } = payload;
	await createTemplate(payload);
	const templates = await getTemplates({ token, userId, report_name: 'games_report' });
	return { templates, savingData: false };
};

const templatesEdit = async (payload: PropertyType) => {
	const { token, userId } = payload;
	await editTemplate(payload);
	const templates = await getTemplates({ token, userId, report_name: 'games_report' });
	return { templates, savingData: false, selectedTemplate: find(templates, ['value', payload.id]) };
};

const templatesDelete = async (payload: PropertyType) => {
	const { token, userId } = payload;
	await deleteTemplate(payload);
	const templates = await getTemplates({ token, userId, report_name: 'games_report' });
	return { templates, savingData: false };
};

const selectTemplate = (payload: PropertyType) => {
	return { selectedTemplate: payload };
};

const loadTemplate = async (payload: PropertyType) => {
	const { token } = payload;
	const filters = await getTemplateById(payload);
	let formatFilters = filters.map((filter) => {
		const newFilter = { ...filter };
		if (filter.name === 'start_date' || filter.name === 'end_date') {
			newFilter.value = new Date(filter.value);
		}
		return newFilter;
	});

	const hasBonusFilter = findIndex(formatFilters, ['name', 'money_type']) !== -1;

	if (!hasBonusFilter) {
		formatFilters = [...formatFilters, { name: 'money_type', value: '0' }];
	}

	return filterData(token, '1', formatFilters);
};

const filtersInit = async (payload: PropertyType) => {
	const { token, filters } = payload;
	const resp = await getFilterOptions({ token, filters });

	return { ...resp.data };
};

const getFiltersUrl = (filters: PropertyType[]) => {
	const urlFragments: string[] = [];

	filters.forEach((f) => {
		const value = f.name == 'start_date' || f.name == 'end_date' ? formatToISO(f.value) : f.value;
		const name = f.name === 'money_type' ? 'show_fun_only' : f.name;
		urlFragments.push(`${name}=${value}`);
	});

	return `?${urlFragments.join('&')}`;
};

const filterData = async (token: string, page: string, filters: PropertyType[]) => {
	const query = filters.length ? getFiltersUrl(filters) : '';

	// Add guard to prevent api call if no required filters, and update filter values
	if (!query.includes('aggregators') || !query.includes('start_date')) {
		return {
			filters,
			filtering: filters.length > 1,
			reportsLoading: false,
			reports: [],
			currentPage: 1,
		};
	}

	const { reports, pagesTotal } = await getGamesReports({
		token,
		page,
		query,
	});

	return {
		reports,
		filters,
		pagesTotal,
		filtering: filters.length > 1,
		currentPage: page,
		reportsLoading: false,
	};
};

const onFilter = async (state: PropertyType, payload: PropertyType) => {
	const { token, page, filters } = payload;
	return filterData(token, page, filters);
};

const onPageUpdate = async (state: PropertyType, payload: PropertyType) => {
	const { filtering } = state;

	if (!filtering) return reportsInit(payload);
	return onFilter(state, { ...payload });
};

const onUpdateColumns = async (payload: PropertyType) => {
	const rawValue = JSON.stringify(payload);
	localStorage.setItem('gamesReport', rawValue);
	return { columns: payload };
};

export const actions: ActionsType = {
	LOADING: (state, payload) => ({ reportsLoading: payload }),
	SAVING: (state, payload) => ({ savingData: payload }),
	FILTERS_INIT: (state, payload) => onRequest(() => filtersInit(payload)),
	PARTNERS_INIT: (state, payload) => partnersInit(payload),
	RATES_INIT: (state, payload) => ratesInit(payload),
	TEMPLATES_INIT: (state, payload) => templatesInit(payload),
	ADD_TEMPLATE: (state, payload) => templatesAdd(payload),
	EDIT_TEMPLATE: (state, payload) => templatesEdit(payload),
	SELECT_TEMPLATE: (state, payload) => selectTemplate(payload),
	DELETE_TEMPLATE: (state, payload) => templatesDelete(payload),
	LOAD_TEMPLATE_DATA: (state, payload) => loadTemplate(payload),
	SELECT_TOTAL: (state, payload) => ({ selectedTotal: payload }),
	PAGE_UPDATE: (state, payload) => onRequest(() => onPageUpdate(state, payload), true),
	INIT_COLUMNS: (state, payload) => ({ columns: payload.columns }),
	UPDATE_INITIAL_COLUMNS: (state, payload) => ({ initialColumns: payload }),
	UPDATE_COLUMNS: (state, payload) => onUpdateColumns(payload),
	UPDATE_ROWS: (state, payload) => ({ rows: payload }),
	UPDATE_FILTERS: (state, payload) => ({ filters: payload }),
	FILTER: (state, payload) => onFilter(state, payload),
	GLOBAL_ERROR: (state, payload) => ({ globalErrors: payload }),
	GLOBAL_SUCCESS: (state, payload) => ({ globalSuccess: payload }),
	MESSAGE_INVALIDATE: () => ({ globalErrors: [], globalSuccess: '', reportsLoading: false, savingData: false }),
};
